import { useState } from "react";
import { Award } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const fmt = (d) => new Date(d + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

function LineForm({ clientId, kind, habitId, initial, onSaved }) {
  const [text, setText] = useState(initial || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const r = await api.post(`/clinician/client/${clientId}/milestone-line`, { text, kind, habit_id: habitId || null }); toast.success(r.data.message); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 space-y-2" data-testid={`milestone-line-form-${kind}`}>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={400} placeholder="One or two lines in your own words — optional"
        data-testid={`milestone-line-text-${kind}`} className="w-full px-3 py-2 rounded-xl border border-stone-300 text-sm focus:border-amber-600 outline-none" />
      <button onClick={save} disabled={busy || text.trim().length < 1} data-testid={`milestone-line-save-${kind}`} className="amber-btn text-xs py-1.5 disabled:opacity-40">{busy ? "Saving…" : initial ? "Update my line" : "Add my line"}</button>
    </div>
  );
}

/** Clinician: approach heads-up (with the line deadline), post-milestone status, and the optional line. */
export function MilestonesPanel({ clientId, data, onChanged }) {
  if (!data || (!data.approaching?.length && !data.earned?.length)) return null;
  return (
    <div className="warm-card p-6" data-testid="clinician-milestones">
      <div className="flex items-center gap-2 mb-3"><Award className="w-4 h-4 text-rose-800" /><h3 className="font-display font-bold text-stone-900">Milestones</h3></div>
      {data.approaching.map((a) => (
        <div key={a.kind + (a.habit_id || "")} className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 mb-3" data-testid={`milestone-approaching-${a.kind}`}>
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800">Approaching</div>
          <p className="text-sm font-semibold text-stone-900 mt-1">{a.detail}</p>
          <p className="text-xs text-stone-600 mt-1">{a.deadline_note} The client sees no countdown — this is for you to make the moment land in session.</p>
          <LineForm clientId={clientId} kind={a.kind} habitId={a.habit_id} initial={a.line} onSaved={onChanged} />
        </div>
      ))}
      {data.earned.map((m) => (
        <div key={m.id} className="rounded-2xl border border-stone-200 p-4 mb-3" data-testid={`milestone-earned-${m.kind}`}>
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <p className="text-sm font-semibold text-stone-900">{m.kind === "century" ? "Century Club" : `Rooted · ${m.habit_title}`}</p>
            <span className="text-xs text-stone-500">earned {fmt(m.earned_at)}</span>
          </div>
          <p className="text-xs text-stone-600 mt-1" data-testid={`milestone-status-${m.kind}`}>
            Note: {m.note_seen_at ? "seen by the member" : m.note_sent_at ? "delivered, not yet opened" : `goes out ${fmt(m.note_due)}`} · Your line: {m.line_status}
          </p>
          {!m.note_sent_at && <LineForm clientId={clientId} kind={m.kind} habitId={m.habit_id} initial={m.clinician_line} onSaved={onChanged} />}
        </div>
      ))}
    </div>
  );
}
